import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api, ApiError, type SecretName } from '../api';
import { ErrorBox } from '../components/Layout';
import type { MerchantProfile } from '../types';

type Secrets = { secretKey?: string; webhookSecret?: string };

const field = (form: FormData, name: string) => String(form.get(name) ?? '').trim();

export function SettingsPage() {
  const [merchant, setMerchant] = useState<MerchantProfile | null | undefined>(undefined);
  const [secrets, setSecrets] = useState<Secrets | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const { merchant: m } = await api.merchant();
      setMerchant(m);
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : 'ໂຫຼດຂໍ້ມູນບໍ່ໄດ້');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const fail = (fallback: string) => (err: unknown) =>
    setError(err instanceof ApiError ? err.message : fallback);

  async function setup(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setError(null);
    try {
      const created = await api.setupMerchant({
        name: field(form, 'name'),
        laoQrPayload: field(form, 'laoQrPayload'),
        destinationAccount: field(form, 'destinationAccount'),
        ...(field(form, 'accountName') ? { accountName: field(form, 'accountName') } : {}),
        ...(field(form, 'webhookUrl') ? { webhookUrl: field(form, 'webhookUrl') } : {}),
      });
      setSecrets({ secretKey: created.secretKey, webhookSecret: created.webhookSecret });
      setMerchant(created.merchant);
    } catch (err: unknown) {
      fail('ຕັ້ງຄ່າບໍ່ສຳເລັດ')(err);
    }
  }

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setError(null);
    setNotice(null);
    try {
      const { merchant: updated } = await api.updateMerchant({
        name: field(form, 'name'),
        laoQrPayload: field(form, 'laoQrPayload'),
        destinationAccount: field(form, 'destinationAccount'),
        accountName: field(form, 'accountName') || null,
        webhookUrl: field(form, 'webhookUrl') || null,
      });
      setMerchant(updated);
      setNotice('ບັນທຶກແລ້ວ');
    } catch (err: unknown) {
      fail('ບັນທຶກບໍ່ສຳເລັດ')(err);
    }
  }

  async function rotate(what: SecretName, label: string) {
    if (!window.confirm(`ອອກ ${label} ໃໝ່? ອັນເກົ່າຈະໃຊ້ບໍ່ໄດ້ທັນທີ — ຕ້ອງໄປປ່ຽນຢູ່ຄລີນິກ/ມືຖືນຳ`)) {
      return;
    }
    try {
      const result = await api.rotate(what);
      if (result.secretKey || result.webhookSecret) {
        setSecrets({
          ...(result.secretKey ? { secretKey: result.secretKey } : {}),
          ...(result.webhookSecret ? { webhookSecret: result.webhookSecret } : {}),
        });
      }
      await load();
      if (what === 'notify-token') setNotice('ອອກ notify token ໃໝ່ແລ້ວ — ຢ່າລືມໄປປ່ຽນໃນມືຖື');
    } catch (err: unknown) {
      fail('ອອກຄ່າໃໝ່ບໍ່ສຳເລັດ')(err);
    }
  }

  if (merchant === undefined) {
    return (
      <>
        <ErrorBox error={error} />
        {!error && <p className="muted">ກຳລັງໂຫຼດ…</p>}
      </>
    );
  }

  return (
    <>
      <ErrorBox error={error} />
      {notice && <p className="notice">{notice}</p>}

      {secrets && (
        <div className="card secret-box">
          <h2 className="section-title">⚠️ ຄ່າລັບ — ສະແດງຄັ້ງດຽວ</h2>
          {secrets.secretKey && (
            <p>
              <strong>secretKey (ໃສ່ໃນຕັ້ງຄ່າຂອງຄລີນິກ)</strong>
              <code>{secrets.secretKey}</code>
            </p>
          )}
          {secrets.webhookSecret && (
            <p>
              <strong>webhookSecret (ໃຊ້ກວດລາຍເຊັນ webhook ຢູ່ຄລີນິກ)</strong>
              <code>{secrets.webhookSecret}</code>
            </p>
          )}
          <button type="button" className="link-button" onClick={() => setSecrets(null)}>
            ຂ້ອຍ copy ເກັບໄວ້ແລ້ວ
          </button>
        </div>
      )}

      {merchant === null ? (
        <form className="card" onSubmit={setup}>
          <h2 className="section-title">ຕັ້ງຄ່າບັນຊີຮັບເງິນ (ຄັ້ງທຳອິດ)</h2>
          <MerchantFields />
          <button type="submit" className="pay">
            ບັນທຶກ ແລະ ອອກ secretKey
          </button>
        </form>
      ) : (
        <>
          <form className="card" onSubmit={save}>
            <h2 className="section-title">ບັນຊີຮັບເງິນ</h2>
            <MerchantFields merchant={merchant} />
            <button type="submit" className="pay">
              ບັນທຶກ
            </button>
          </form>

          <section className="card">
            <h2 className="section-title">ເຊື່ອມຕໍ່ກັບຄລີນິກ</h2>
            <dl className="summary">
              <dt>API URL</dt>
              <dd>
                <code>{merchant.apiUrl}/gateway/v1</code>
              </dd>
              <dt>Header</dt>
              <dd>
                <code>secretKey: {merchant.keyPrefix}…</code>
              </dd>
              <dt>Webhook</dt>
              <dd>{merchant.webhookUrl ?? 'ຍັງບໍ່ໄດ້ຕັ້ງ'}</dd>
            </dl>
            <div className="admin-actions">
              <button
                type="button"
                className="link-button"
                onClick={() => rotate('secret-key', 'secret key')}
              >
                ອອກ secret key ໃໝ່
              </button>
              <button
                type="button"
                className="link-button"
                onClick={() => rotate('webhook-secret', 'webhook secret')}
              >
                ອອກ webhook secret ໃໝ່
              </button>
            </div>
          </section>

          <section className="card">
            <h2 className="section-title">ຕັ້ງຄ່າມືຖືໃຫ້ຢືນຢັນອັດຕະໂນມັດ</h2>
            <p className="hint slip-hint">
              ໃຫ້ມືຖືທີ່ລົງ BCEL One ອ່ານແຈ້ງເຕືອນເງິນເຂົ້າ ແລ້ວສົ່ງມາໃຫ້ລະບົບ ດ້ວຍ{' '}
              <strong>MacroDroid</strong> (ຟຣີ, Android)
            </p>
            <ol className="steps">
              <li>ລົງ MacroDroid ຈາກ Play Store ແລ້ວສ້າງ Macro ໃໝ່</li>
              <li>
                <strong>Trigger</strong> → Device Events → Notification → Notification Received →
                ເລືອກແອັບ <strong>BCEL One</strong>
              </li>
              <li>
                <strong>Action</strong> → Applications → HTTP Request ແລ້ວຕັ້ງຄ່າລຸ່ມນີ້
              </li>
              <li>ອະນຸຍາດສິດອ່ານ notification ໃຫ້ MacroDroid ແລ້ວບັນທຶກ</li>
            </ol>
            <dl className="summary">
              <dt>Method</dt>
              <dd>POST</dd>
              <dt>URL</dt>
              <dd>
                <code>{merchant.notifyUrl}</code>
              </dd>
              <dt>Content Type</dt>
              <dd>text/plain</dd>
              <dt>Body</dt>
              <dd>
                <code>[notification_text]</code>
              </dd>
              <dt>Header</dt>
              <dd>
                <code>x-notify-token: {merchant.notifyToken ?? '—'}</code>
              </dd>
            </dl>
            <p className="hint slip-hint">
              ທົດສອບໂດຍຕື່ມ <code>?dryRun=1</code> ທ້າຍ URL — ລະບົບຈະບອກວ່າແກະຍອດໄດ້ຫຍັງ ໂດຍບໍ່ແຕະລາຍການຈິງ
            </p>
            <button
              type="button"
              className="link-button"
              onClick={() => rotate('notify-token', 'notify token')}
            >
              ອອກ notify token ໃໝ່
            </button>
          </section>
        </>
      )}
    </>
  );
}

function MerchantFields({ merchant }: { merchant?: MerchantProfile }) {
  return (
    <>
      <label>
        ຊື່
        <input name="name" maxLength={100} required defaultValue={merchant?.name ?? 'Clinic'} />
      </label>
      <label>
        QR payload ຈາກ BCEL One → My QR
        <input
          name="laoQrPayload"
          required
          placeholder="000201..."
          defaultValue={merchant?.laoQrPayload ?? ''}
        />
      </label>
      <p className="hint slip-hint">
        ເປີດ BCEL One → My QR → ບັນທຶກຮູບ → ສະແກນດ້ວຍແອັບອ່ານ QR → copy ຂໍ້ຄວາມທີ່ຂຶ້ນຕົ້ນດ້ວຍ 000201 ມາທັງໝົດ
      </p>
      <label>
        ເລກບັນຊີປາຍທາງ (ຕາມສະລິບ BCEL)
        <input
          name="destinationAccount"
          required
          placeholder="092-12-XXXXXX70"
          defaultValue={merchant?.destinationAccount ?? ''}
        />
      </label>
      <label>
        ຊື່ບັນຊີ (ບໍ່ບັງຄັບ)
        <input name="accountName" maxLength={100} defaultValue={merchant?.accountName ?? ''} />
      </label>
      <label>
        Webhook URL ຂອງຄລີນິກ (ບໍ່ບັງຄັບ)
        <input
          name="webhookUrl"
          type="url"
          placeholder="https://api.clinic.example/payments/webhook"
          defaultValue={merchant?.webhookUrl ?? ''}
        />
      </label>
    </>
  );
}
